import _ from 'lodash';

function isDateString(value: any): boolean {
  return (
    typeof value === 'string' &&
      !Number.isNaN(Date.parse(value)) &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?$/.test(value)
  );
}

function normalizeDateStringToSeconds(dateStr: string): string {
  // Ensure all strings are parsed as UTC by appending 'Z' if missing
  const fixedStr = dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`;

  const date = new Date(fixedStr);
  if (Number.isNaN(date.getTime())) return '';

  // Format to "yyyy-MM-ddTHH:mm:ss"
  return date.toISOString().slice(0, 19);
}

export function getFormChanges<T>(
  currentForm: T,
  previousForm: T,
  stripUnchanged: boolean = false,
): T | Partial<T> | null | undefined {
  // Normalize dates before comparing
  if (typeof currentForm === 'string' &&
    typeof previousForm === 'string' &&
    isDateString(currentForm) &&
    isDateString(previousForm)) {
    const normalizedCurrent = normalizeDateStringToSeconds(currentForm);
    const normalizedPrevious = normalizeDateStringToSeconds(previousForm);

    if (normalizedCurrent !== normalizedPrevious) {
      return currentForm;
    }
    return stripUnchanged ? undefined : null;
  }

  if (_.isEqual(currentForm, previousForm)) {
    if (stripUnchanged) {
      return undefined;
    }
    return null;
  }

  if (Array.isArray(currentForm) && Array.isArray(previousForm)) {
    if (_.isEqual(currentForm, previousForm)) {
      if (stripUnchanged) {
        return undefined;
      }
      return null;
    }
    return currentForm;
  }

  if (
    typeof currentForm === 'object' && currentForm !== null &&
    typeof previousForm === 'object' && previousForm !== null
  ) {
    const result: Record<string, unknown> = {};
    const allKeys = new Set([
      ...Object.keys(currentForm),
      ...Object.keys(previousForm),
    ]);

    for (const key of allKeys) {
      const currentVal = (currentForm as Record<string, unknown>)[key];
      const previousVal = (previousForm as Record<string, unknown>)[key];

      const diff = getFormChanges(currentVal, previousVal, stripUnchanged);
      if (diff !== undefined) {
        result[key] = diff;
      }
    }

    if (_.isEmpty(result)) {
      if (stripUnchanged) {
        return undefined;
      }
      return null;
    }
    return result as Partial<T>;
  }

  if (currentForm !== previousForm) {
    return currentForm;
  }
  if (stripUnchanged) {
    return undefined;
  }
  return null;
}
